/**
 * Bounded Remote Console sync: after a software OTA command, poll Fleet
 * package GETs until device-reported Installed matches Available (or timeout).
 *
 * Never fabricates installed versions — only consumes package-status responses.
 */

export const SOFTWARE_INVENTORY_OTA_POLL_MS = 3_000;
/** Cap post-command inventory convergence wait (device report + Fleet persist). */
export const SOFTWARE_INVENTORY_OTA_SYNC_TIMEOUT_MS = 3 * 60_000;

/** Which package inventory lane was just OTA'd (Remote Console tab identity). */
export type SoftwareInventoryOtaSyncTarget =
  | "assistant"
  | "player"
  | "screensaver";

export type InventorySyncPackageStatus = {
  installed?: { versionCode?: number | null } | null;
  available?: { versionCode?: number | null } | null;
};

export function installedMatchesAvailable(
  status: InventorySyncPackageStatus | null | undefined
): boolean {
  const installed = Number(status?.installed?.versionCode);
  const available = Number(status?.available?.versionCode);
  if (!Number.isFinite(installed) || installed <= 0) return false;
  if (!Number.isFinite(available) || available <= 0) return false;
  return installed === available;
}

/**
 * Continue polling while sync is active, before timeout, until installed
 * catches Available (device inventory truth). Failed OTAs should clear sync
 * from the caller so this never assumes success.
 */
export function shouldContinueSoftwareInventoryOtaSync(args: {
  syncActive: boolean;
  status: InventorySyncPackageStatus | null | undefined;
  startedAtMs: number;
  nowMs?: number;
  timeoutMs?: number;
}): boolean {
  if (!args.syncActive) return false;
  const now = args.nowMs ?? Date.now();
  const timeout = args.timeoutMs ?? SOFTWARE_INVENTORY_OTA_SYNC_TIMEOUT_MS;
  if (now - args.startedAtMs >= timeout) return false;
  if (installedMatchesAvailable(args.status)) return false;
  return true;
}

/**
 * @deprecated Prefer {@link resolveSoftwareInventoryFleetPollingIntervalMs}.
 * Kept for OTA-only call sites; returns 3s while syncing else 0.
 */
export function softwareInventoryOtaPollingIntervalMs(
  syncActive: boolean
): number {
  return syncActive ? SOFTWARE_INVENTORY_OTA_POLL_MS : 0;
}
